import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  ResponsiveContainer,
} from 'recharts';
import { DynamicToolTip } from '../DynamicToolTip/DynamicToolTip';
import i from '@/assets/svg/emsat/i.svg';
import type { GradeWiseParticipationItem } from '@/types/schoolReport';

interface GradeWiseParticipationChartProps {
  data?: GradeWiseParticipationItem[];
}

interface ChartRow {
  grade: string;
  students: number;
  registration: number;
  nonParticipated: number;
}

export default function GradeWiseParticipationChart({
  data = [],
}: GradeWiseParticipationChartProps) {
  const chartData: ChartRow[] = data.map((item) => ({
    grade: item.grade.replace('Grade-', ''),
    students: item.participated,
    registration: Math.max(item.registered, item.participated),
    nonParticipated: Math.max(item.registered, item.participated) - item.participated,
  }));

  const getYTicks = (rows: ChartRow[]) => {
    const maxOriginal = Math.max(
      ...rows.map((item) => Math.max(item.students, item.registration)),
      5
    );

    if (maxOriginal <= 5) return [0, 1, 2, 3, 4, 5];

    const step = Math.ceil(maxOriginal / 5);
    const ticks: number[] = [];
    for (let i = 0; i <= maxOriginal; i += step) {
      ticks.push(Math.round(i));
    }

    if (!ticks.includes(maxOriginal)) ticks.push(maxOriginal);
    return ticks;
  };

  const yTicks = getYTicks(chartData);
  const yDomainMax = Math.max(...yTicks);

  const axisLabelStyles = {
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '100%',
    letterSpacing: '0%',
    textAlign: 'center' as const,
    color: '#000000',
  };

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        position: 'relative',
      }}
    >
      <h3
        style={{
          margin: '0 0 15px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: '#333',
        }}
      >
        Grade Wise Participation{' '}
        <DynamicToolTip
          content="This section displays the number of students who registered and the number who participated in the event, for each grade."
          onClick={() => undefined}
        >
          <span>
            {' '}
            <img className="EMRe_info" src={i} alt="i" />
          </span>
        </DynamicToolTip>
      </h3>
      <div style={{ width: '100%', height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 20, left: 40, bottom: 30 }}
            barSize={24}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis
              dataKey="grade"
              tick={{ fill: '#000000', fontSize: 11 }}
              axisLine={{ stroke: '#ddd' }}
              tickLine={false}
              label={{
                value: 'Grade',
                position: 'insideBottom',
                fill: '#263643',
                offset: -5,
                ...axisLabelStyles,
              }}
            />
            <YAxis
              domain={[0, yDomainMax]}
              ticks={yTicks}
              tick={{ fill: '#000000', fontSize: 11 }}
              axisLine={{ stroke: '#ddd' }}
              tickLine={false}
              label={{
                value: 'No. of students participated',
                angle: -90,
                position: 'insideLeft',
                offset: 15,
                fill: '#263643',
                ...axisLabelStyles,
                style: {
                  ...axisLabelStyles,
                  textAnchor: 'middle',
                },
              }}
            />
            <Tooltip
              contentStyle={{
                fontFamily: 'Montserrat',
                fontWeight: 500,
                fontSize: '12px',
                color: '#000000',
              }}
            />
            <Bar
              dataKey="registration"
              fill="#3C8DCB"
              name="Registered"
              radius={[4, 4, 0, 0]}
              label={{
                position: 'top',
                fill: '#000000',
                fontSize: 11,
                fontFamily: 'Montserrat',
                fontWeight: 500,
              }}
            />
            <Bar
              dataKey="students"
              fill="#82ECC8"
              name="Participated"
              radius={[4, 4, 0, 0]}
              label={{
                position: 'top',
                fill: '#000000',
                fontSize: 11,
                fontFamily: 'Montserrat',
                fontWeight: 500,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: '#888',
          position: 'absolute',
          bottom: '10px',
          display: 'flex',
          gap: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div
            style={{
              width: 10,
              height: 10,
              background: '#3C8DCB',
              borderRadius: '2px',
            }}
          />
          Registered
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div
            style={{
              width: 10,
              height: 10,
              background: '#82ECC8',
              borderRadius: '2px',
            }}
          />
          Participated
        </div>
      </div>
    </div>
  );
}
